import { INPUT_TYPES } from "@miplatform/constants";

/**
 * Gets updated fields and their values.
 *
 * @param {Object} dirtyFields - An object where keys are field names and values indicate if they are dirty.
 * @param {Object} values - An object with field names as keys and their corresponding values.
 * @returns {Object} An object with field names as keys and their corresponding values.
 */
export const getUpdatedFields = (dirtyFields, values) => {
  if (Object.keys(dirtyFields).length === 0) {
    return {};
  }
  const updatedFields = Object.keys(dirtyFields).reduce((acc, field) => {
    if (Object.prototype.hasOwnProperty.call(values, field)) {
      acc[field] = values[field];
    }
    return acc;
  }, {});
  updatedFields.modifiedOn = values?.modifiedOn;
  return updatedFields;
};

export const isIncludedInDirtyFields = (dirtyFields, field) => {
  return Object.prototype.hasOwnProperty.call(dirtyFields, field);
};

const isFieldRequired = (field) => {
  return !field.isOptional();
};

/**
 * Extracts the names of required fields from a schema.
 *
 * @param {Object} schema - The schema object containing field definitions.
 * @param {Object} schema.shape - The shape property of the schema, which maps field names to their definitions.
 * @returns {string[]} An array of field names that are marked as required in the schema.
 */
export const extractRequiredFieldNamesFromSchema = (schema) => {
  const { shape } = schema;
  const requiredFields = [];

  Object.keys(shape).forEach((key) => {
    const field = shape[key];
    if (isFieldRequired(field)) {
      requiredFields.push(key);
    }
  });

  return requiredFields;
};

/**
 * Updates a fieldset to mark fields as required based on a list of required field names.
 *
 * @param {Object[]} fieldset - An array of field objects representing the fieldset.
 * @param {string[]} requiredFields - An array of field names that should be marked as required.
 * @param {string} fieldset[].name - The name of the field.
 * @param {boolean} [fieldset[].required] - Whether the field is required.
 * @returns {Object[]} The updated fieldset with the `required` property set to `true` for fields listed in `requiredFields`.
 */
export const applyRequiredFieldsToFieldset = (fieldset, requiredFields) => {
  return fieldset.map((field) => {
    if (requiredFields.includes(field.name)) {
      return { ...field, required: true };
    }
    return field;
  });
};

/**
 * Updates a fieldset based on the required fields defined in a schema.
 *
 * @param {Object} schema - The schema object containing field definitions.
 * @param {Object[]} fieldset - An array of field objects representing the fieldset.
 * @param {Object} schema.shape - The shape property of the schema, which maps field names to their definitions.
 * @param {string} fieldset[].name - The name of the field.
 * @param {boolean} [fieldset[].required] - Whether the field is required.
 * @returns {Object[]} The updated fieldset with the `required` property set according to the schema's requirements.
 */
export const updateFieldsetFromSchemaRequirements = (schema, fieldset) => {
  const requiredFields = extractRequiredFieldNamesFromSchema(schema);
  return applyRequiredFieldsToFieldset(fieldset, requiredFields);
};

export const getAllFieldValues = (details, edits) => {
  const mergeDeep = (target, source) => {
    Object.keys(source).forEach((key) => {
      if (Array.isArray(source[key])) {
        target[key] = [...source[key]];
      } else if (source[key] instanceof Object && key in target) {
        target[key] = target[key] instanceof Object ? target[key] : {};
        target[key] = mergeDeep(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    });
    return target;
  };

  return mergeDeep(details ? JSON.parse(JSON.stringify(details)) : {}, edits);
};

export const validateField = (value, rule) => {
  return rule.safeParse(typeof value === "string" ? value.trim() : value);
};

export const handleValidationResult = (
  key,
  success,
  error,
  validateResult,
  currentErrors,
  getAllErrors = false
) => {
  if (!success) {
    const messages = error.issues.map((issue) => issue.message);
    const message = getAllErrors ? messages : messages.slice(0, 1);
    validateResult[key] = {
      message,
    };
    return;
  }
  if (currentErrors[key]) {
    delete currentErrors[key];
  }
};

export const hasValidationErrors = (currentErrors, validateResult) => {
  return (
    Object.keys(currentErrors).length > 0 ||
    Object.keys(validateResult).length > 0
  );
};

export const isZodFieldRequired = (field) => {
  return !field.isOptional();
};

export const getStyledFieldSet = (fieldset) => {
  // controls display of placeholders and label behavior across text/number/select inputs
  return fieldset.map((field) => {
    if (
      field.fieldType === INPUT_TYPES.TEXTBOX ||
      field.fieldType === INPUT_TYPES.NUMBER
    ) {
      return {
        inputProps: {
          sx: {
            "&::placeholder": {
              opacity: 1,
            },
          },
        },
        ...field,
      };
    }

    return field;
  });
};
